/** @jsxImportSource @emotion/react */
import { Box, useTheme } from '@mui/material'
import { PropertyInformation } from '@app/components/Property/types'
import { useMemo } from 'react'
import {
  noDetailsStyle,
  scrollContainer,
  tableParent,
  tableStyle,
  tabParent,
  thTdKeyStyle,
  thTdValueStyle,
} from './SideDrawerContent/DetailsTab/DetailsTab.styles'
import { SlideInFooter } from './SideDrawerContent/SlideInFooter'
import { useAtomValue } from 'jotai'
import { canExportAtom } from '@app/hooks/useUserProfile'

const PropertyDetails = ({
  data,
  loading,
}: {
  data: PropertyInformation | null
  loading: boolean
}) => {
  const theme = useTheme()
  const canExport = useAtomValue(canExportAtom)

  const remainingInfo = useMemo(
    () =>
      Object.entries(data || {})
        .filter(
          ([key, value]) =>
            key !== 'Legal description' && key !== 'Address' && key !== 'Address(es)' && value,
        )
        .toSorted((a, b) => a[0].localeCompare(b[0])),
    [data],
  )
  // comments below from Dean, I moved it to PropertyDetails during refactoring
  // These groups will be determined by the pex config json in time
  // For now, to demo groups, they're being generated based on the key names
  // This needs to be updated after the MVP
  const categories = useMemo(() => {
    return remainingInfo.reduce(
      (acc, [key, value]) => {
        const lowerKey = key.toLowerCase()
        if (
          lowerKey.includes('value') ||
          lowerKey.includes('cost') ||
          (typeof value === 'string' && value.includes('$'))
        ) {
          if (typeof value === 'string' || typeof value === 'number') {
            acc.Value[key] = value
          }
        } else if (lowerKey.includes('area')) {
          if (typeof value === 'string' || typeof value === 'number') {
            acc.Area[key] = value
          }
        } else {
          if (typeof value === 'string' || typeof value === 'number') {
            acc.Other[key] = value
          }
        }
        return acc
      },
      {
        Value: {} as Record<string, string | number>,
        Area: {} as Record<string, string | number>,
        Other: {} as Record<string, string | number>,
      },
    )
  }, [remainingInfo])

  if (!data) {
    return <Box css={noDetailsStyle}>No details available for this property.</Box>
  }

  return (
    <Box css={tabParent(theme, loading)}>
      <Box css={scrollContainer()}>
        <Box css={{ padding: '16px 0' }}>
          <h2>Property Details</h2>
          {data.LegalDescription && <b>{data.LegalDescription}</b>}
          {data.Address && <p>{data.Address}</p>}
        </Box>
        {remainingInfo.length > 0 && (
          <Box css={tableParent}>
            {Object.entries(categories).map(([category, details]) =>
              Object.keys(details).length > 0 ? (
                <Box key={category}>
                  <h3>{category}</h3>
                  <table css={tableStyle}>
                    <tbody>
                      {Object.entries(details).map(([key, value]) => (
                        <tr key={key}>
                          <td css={thTdKeyStyle}>{key}</td>
                          <td css={thTdValueStyle}>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              ) : null,
            )}
          </Box>
        )}
      </Box>
      {canExport && <SlideInFooter open={remainingInfo.length != 0} />}
    </Box>
  )
}

export default PropertyDetails
